import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { format } from 'date-fns';
import { db } from '../config/firebase';
import { ArchiveSale, SYSTEM_LABEL, toDate, sellThrough, mapLink } from '../types/archive.types';

/**
 * PAST AUCTIONS — public, no sign-in. Reverse chronological.
 *
 * Reads ONLY archiveSales (see types/archive.types.ts): a projection with no customer data, written by
 * publish-archive.mjs. A sale appears here when it has a row there — adding a sale (or 126 migrated HiBid
 * sales) is adding rows, not changing this page. It never reads auctions/{id}.
 *
 * Pagination: not yet. Add it when there are more than about twenty rows.
 */
const PastAuctions: React.FC = () => {
  const [sales, setSales] = useState<ArchiveSale[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getDocs(query(collection(db, 'archiveSales'), orderBy('endDate', 'desc')))
      .then((snap) => setSales(snap.docs
        .map((d) => ({ ...(d.data() as Omit<ArchiveSale, 'id'>), id: d.id }))
        .filter((s) => s.systemOfRecord in SYSTEM_LABEL)))
      .catch((err) => { console.error('Past auctions load error:', err); setError(err.message); });
  }, []);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <header>
        <h1 className="text-3xl font-bold">Past Auctions</h1>
        <p className="text-gray-600 mt-1">Every sale we have closed, with what sold and at what price.</p>
      </header>

      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-red-800">Could not load past sales: {error}</div>
      )}
      {sales === null && !error && <p className="text-gray-600">Loading past sales…</p>}
      {sales !== null && sales.length === 0 && (
        <div className="rounded-lg border border-gray-300 bg-gray-50 p-6">
          <p className="font-semibold text-gray-900">No past sales published yet.</p>
        </div>
      )}

      {sales !== null && sales.length > 0 && (
        <ul className="space-y-4">
          {sales.map((s) => <SaleCard key={s.id} sale={s} />)}
        </ul>
      )}
    </div>
  );
};

const SaleCard: React.FC<{ sale: ArchiveSale }> = ({ sale: s }) => {
  const start = toDate(s.startDate), end = toDate(s.endDate);
  const pct = sellThrough(s);
  const dates = start && end ? `${format(start, 'M/d/yyyy')} - ${format(end, 'M/d/yyyy')}` : end ? format(end, 'M/d/yyyy') : null;
  return (
    <li className="rounded-lg border border-gray-200 bg-white overflow-hidden flex flex-col sm:flex-row">
      {s.coverImage && (
        <Link to={`/past/${s.id}`} className="sm:w-48 shrink-0 bg-gray-100">
          <img src={s.coverImage} alt="" loading="lazy" className="w-full h-48 sm:h-full object-cover" />
        </Link>
      )}
      <div className="p-4 sm:p-5 flex-1 min-w-0 space-y-2">
        <div className="flex flex-wrap gap-2">
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-semibold">Closed</span>
          {s.hasResults && <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-800 font-semibold">Results</span>}
          {s.systemOfRecord === 'auctionsaas' && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 font-semibold">Ran on {SYSTEM_LABEL.auctionsaas}</span>
          )}
        </div>
        <h2 className="text-xl font-bold text-gray-900">
          <Link to={`/past/${s.id}`} className="hover:text-primary-600">{s.title}</Link>
        </h2>
        {dates && <p className="text-sm text-gray-700"><span className="font-semibold">Date(s)</span> {dates}</p>}
        {s.venue && (
          <p className="text-sm text-gray-700">
            <a href={mapLink(s.venue)} target="_blank" rel="noopener noreferrer" className="hover:underline">📍 {s.venue}</a>
          </p>
        )}
        {s.description && <p className="text-sm text-gray-600 line-clamp-3 whitespace-pre-line">{s.description}</p>}
        <div className="flex flex-wrap items-end justify-between gap-3 pt-1">
          <p className="text-sm text-gray-800">
            <span className="font-semibold">{s.lotCount} lots</span>
            {pct != null && <> · {s.lotsSold} sold · <span className="font-semibold">{pct}% sell-through</span></>}
          </p>
          <Link to={`/past/${s.id}`} className="btn-primary text-sm">
            {s.hasResults ? 'View Results' : 'View Catalog'} ({s.lotCount} Lots)
          </Link>
        </div>
      </div>
    </li>
  );
};

export default PastAuctions;
